import { AlgoliaProductCardRenderer } from '@lyle/algolia-product-card'
import {
  algoliaProductAttributes,
  AlgoliaProductFetcher,
} from '@lyle/algolia-product-fetcher'
import {
  buildAlgoliaFilters,
  clamp,
  defaultSearchFilterOptions,
  isPriceAttribute,
  rangeBounds,
  rangePercent,
  rangeStyle,
  rangeTicks,
  resolveFilterOptions,
} from '@lyle/algolia-filter-utils'
import {
  algoliaUserToken,
  escapeHtml,
  parseJson,
  trackAlgoliaProductConversion,
  trackAlgoliaProductClick,
} from '@lyle/algolia-product-utils'
import { getLocalisedRoute, translateWeglotSearchRequests } from '@lyle/weglot'

const ALGOLIA_QUERY_ENDPOINT = appId => {
  return `https://${appId}-dsn.algolia.net/1/indexes/*/queries`
}
const DRAWER_ANIMATION_MS = 300
const RESOURCE_ATTRIBUTES = [
  'blog',
  'body_html_safe',
  'handle',
  'image',
  'objectID',
  'title',
]
class LyleMainSearch extends HTMLElement {
  #abortController = null
  #cardRenderer = null
  #config = null
  #drawerAnimationTimer = null
  #fetcher = null
  #quickView = null
  #quickViewPromise = null
  #requestId = 0
  #state = {
    drawerOpen: false,
    facets: {},
    openPanel: null,
    pageSize: 16,
    pages: {
      product: 0,
      collection: 0,
      article: 0,
      page: 0,
    },
    query: '',
    ranges: {},
    resource: 'product',
    sortIndex: '',
  }
  #swatchRequestId = 0

  connectedCallback() {
    this.#config = this.#parseConfig()

    if (!this.#config) {
      return
    }

    this.#abortController = new AbortController()
    this.#state.query = this.#queryFromUrl() || this.#config.initialQuery || ''
    this.#state.pageSize =
      Number(this.#config.pagination.defaultPageSize) || this.#state.pageSize
    const sortId = new URLSearchParams(window.location.search).get('sort')
    const sort = this.#config.algolia.sortByOptions.find(
      option => option.id === sortId
    )
    this.#state.sortIndex = sort?.value || this.#config.algolia.indexName
    this.#state.pages.product = this.#pageFromUrl()
    this.#state.facets = this.#facetsFromUrl()
    this.#state.ranges = this.#rangesFromUrl()
    this.#cardRenderer = new AlgoliaProductCardRenderer({
      config: this.#config,
      icons: window.icons || {},
      isMobile: () => this.#isMobile(),
    })
    this.#fetcher = new AlgoliaProductFetcher({
      config: this.#config,
      signal: this.#abortController.signal,
    })

    this.#bindEvents()
    void this.#search()
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    document.documentElement.classList.remove('overflow-hidden')

    if (this.#drawerAnimationTimer) {
      clearTimeout(this.#drawerAnimationTimer)
    }

    document.body.style.overflow = ''
  }

  #bindEvents() {
    this.addEventListener('input', this.#handleRangeInput, {
      signal: this.#abortController.signal,
    })
    this.addEventListener('change', this.#handleRangeChange, {
      signal: this.#abortController.signal,
    })
    this.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    })
  }

  #handleRangeInput = event => {
    const input = event.target.closest('[data-range-input], [data-range-text]')

    if (!input) {
      return
    }

    this.#syncRangeControls(input)
  }

  #handleRangeChange = event => {
    const input = event.target.closest('[data-range-input], [data-range-text]')

    if (!input) {
      return
    }

    this.#syncRangeControls(input, { commit: true })
  }

  #handleClick = event => {
    const target = event.target
    const resourceButton = target.closest('[data-resource-type]')
    const pageButton = target.closest('[data-search-page]')
    const mobileFiltersButton = target.closest(
      '[data-main-search-mobile-filters]'
    )
    const drawerClose = target.closest('[data-main-search-drawer-close]')
    const drawerViewResults = target.closest(
      '.main-search__drawer-view-results'
    )
    const sortButton = target.closest('[data-sort-value]')
    const rangeReset = target.closest('[data-range-reset]')
    const facetButton = target.closest('[data-facet-value]')
    const activeFilter = target.closest('[data-active-filter]')
    const clearAll = target.closest('[data-clear-all]')
    const panelToggle = target.closest('[data-search-panel-toggle]')
    const popularButton = target.closest('[data-popular-search]')
    const productLink = target.closest('[data-product-link]')
    const quickView = target.closest('[data-quick-view]')
    const quickViewClose = target.closest('[data-quick-view-close]')
    const quickViewAdd = target.closest('[data-quick-view-add]')
    const quickViewArrow = target.closest('[data-quick-view-arrow]')
    const quickViewSize = target.closest('[data-quick-view-size]')
    const quickViewSwatch = target.closest('[data-quick-view-swatch]')
    const swatch = target.closest('[data-card-swatch]')

    if (resourceButton) {
      event.preventDefault()
      this.#state.resource = resourceButton.dataset.resourceType || 'product'
      this.#renderPanels()
      return
    }

    if (pageButton) {
      event.preventDefault()
      const resource =
        pageButton.dataset.searchPageResource || this.#state.resource
      this.#state.pages[resource] = Number(pageButton.dataset.searchPage) || 0
      this.#writeSearchUrl()
      void this.#search()
      return
    }

    if (mobileFiltersButton) {
      event.preventDefault()
      this.#openDrawer()
      return
    }

    if (drawerClose || drawerViewResults) {
      event.preventDefault()
      this.#closeDrawer()
      return
    }

    if (sortButton) {
      event.preventDefault()
      this.#state.sortIndex =
        sortButton.dataset.sortValue || this.#state.sortIndex
      this.#state.pages.product = 0
      this.#closePanels()
      this.#writeSearchUrl()
      void this.#search()
      return
    }

    if (rangeReset) {
      event.preventDefault()
      this.#resetRange(rangeReset.dataset.rangeReset || '')
      return
    }

    if (facetButton) {
      event.preventDefault()
      this.#toggleFacet(
        facetButton.dataset.facetAttribute || '',
        facetButton.dataset.facetValue || ''
      )
      return
    }

    if (activeFilter) {
      event.preventDefault()
      this.#removeActiveFilter(
        activeFilter.dataset.filterAttribute || '',
        activeFilter.dataset.filterValue || '',
        activeFilter.dataset.filterRangeSide || ''
      )
      return
    }

    if (clearAll) {
      event.preventDefault()
      this.#clearAllFilters()
      return
    }

    if (panelToggle) {
      event.preventDefault()
      this.#togglePanel(panelToggle.dataset.searchPanelToggle || '')
      return
    }

    if (popularButton) {
      event.preventDefault()
      this.#state.query = popularButton.dataset.popularSearch || ''
      this.#resetPages()
      this.#writeSearchUrl()
      void this.#search()
      return
    }

    if (quickView) {
      event.preventDefault()
      void this.#openQuickView(quickView)
      return
    }

    if (quickViewClose) {
      event.preventDefault()
      this.#closeQuickView()
      return
    }

    if (quickViewAdd) {
      event.preventDefault()
      this.#addQuickViewProduct(quickViewAdd)
      return
    }

    if (quickViewArrow) {
      event.preventDefault()
      this.#scrollQuickViewGallery()
      return
    }

    if (quickViewSize) {
      event.preventDefault()
      this.#selectQuickViewVariant(quickViewSize)
      return
    }

    if (quickViewSwatch) {
      event.preventDefault()
      this.#selectQuickViewSwatch(quickViewSwatch)
      return
    }

    if (swatch) {
      event.preventDefault()
      this.#selectSwatch(swatch)
      return
    }

    if (productLink) {
      this.#rememberSelectedProduct(productLink)
      void trackAlgoliaProductClick(
        this.#config,
        this.#state.sortIndex,
        productLink
      )
    }
  }

  async #search() {
    const requestId = ++this.#requestId

    if (!this.#hasAlgoliaConfig()) {
      this.#renderError()
      return
    }

    this.#setLoading(true)

    try {
      const requests = await translateWeglotSearchRequests(
        this.#buildRequests()
      )
      const response = await fetch(
        ALGOLIA_QUERY_ENDPOINT(this.#config.algolia.appId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Algolia-API-Key': this.#config.algolia.apiKey,
            'X-Algolia-Application-Id': this.#config.algolia.appId,
          },
          signal: this.#abortController.signal,
          body: JSON.stringify({ requests }),
        }
      )

      if (!response.ok) {
        throw new Error('Algolia request failed')
      }

      const payload = await response.json()

      if (requestId !== this.#requestId) {
        return
      }

      this.#render(payload.results || [])
    } catch {
      if (requestId === this.#requestId) {
        this.#renderError()
      }
    } finally {
      if (requestId === this.#requestId) {
        this.#setLoading(false)
      }
    }
  }

  #buildRequests() {
    const indexes = this.#config.algolia.indexes
    return [
      {
        indexName: this.#state.sortIndex || indexes.products,
        params: this.#productParams().toString(),
      },
      {
        indexName: this.#state.sortIndex || indexes.products,
        params: this.#productFacetParams().toString(),
      },
      {
        indexName: indexes.collections,
        params: this.#resourceParams('collection').toString(),
      },
      {
        indexName: indexes.articles,
        params: this.#resourceParams('article').toString(),
      },
      {
        indexName: indexes.pages,
        params: this.#resourceParams('page').toString(),
      },
      {
        indexName: indexes.suggestions,
        params: this.#suggestionParams().toString(),
      },
      {
        indexName: indexes.trendingProducts,
        params: this.#trendingParams().toString(),
      },
    ]
  }

  #productParams() {
    const params = this.#baseParams('product')
    params.set('hitsPerPage', String(this.#state.pageSize))
    params.set('page', String(this.#state.pages.product))
    params.set('filters', this.#buildFilters())
    params.set(
      'facets',
      JSON.stringify(this.#resolvedFilterOptions().map(filter => filter.value))
    )
    params.set('maxValuesPerFacet', '70')
    params.set('distinct', 'true')
    params.set('facetingAfterDistinct', 'true')
    params.set(
      'attributesToRetrieve',
      JSON.stringify(algoliaProductAttributes(this.#config.market.handle))
    )
    params.set('attributesToHighlight', '[]')
    return params
  }

  #productFacetParams() {
    const params = this.#baseParams('product')
    params.set('hitsPerPage', '0')
    params.set('page', '0')
    params.set('filters', this.#buildFilters({ includeRefinements: false }))
    params.set(
      'facets',
      JSON.stringify(this.#resolvedFilterOptions().map(filter => filter.value))
    )
    params.set('maxValuesPerFacet', '70')
    params.set('distinct', 'true')
    params.set('facetingAfterDistinct', 'true')
    return params
  }

  #buildFilters(options = {}) {
    return buildAlgoliaFilters({
      baseFilters: [this.#config.algolia.defaultFilters],
      facets: this.#state.facets,
      ranges: this.#state.ranges,
      ...options,
    })
  }

  #resourceParams(resource) {
    const params = this.#baseParams(resource)
    params.set('hitsPerPage', String(this.#resourcePageSize()))
    params.set('page', String(this.#state.pages[resource] || 0))
    params.set('attributesToRetrieve', JSON.stringify(RESOURCE_ATTRIBUTES))
    return params
  }

  #suggestionParams() {
    const params = new URLSearchParams()
    params.set('query', '')
    params.set('hitsPerPage', '5')
    params.set('attributesToRetrieve', JSON.stringify(['objectID', 'query']))
    return params
  }

  #trendingParams() {
    const params = new URLSearchParams()
    params.set('query', '')
    params.set('hitsPerPage', '4')
    params.set('filters', this.#config.algolia.defaultFilters)
    params.set('distinct', 'true')
    params.set(
      'attributesToRetrieve',
      JSON.stringify(algoliaProductAttributes(this.#config.market.handle))
    )
    params.set('attributesToHighlight', '[]')
    return params
  }

  #resourcePageSize() {
    const pageSize = Number(this.#config.pagination.resourcePageSize)

    if (!Number.isInteger(pageSize) || pageSize < 1) {
      return 12
    }

    return pageSize
  }

  #baseParams(resource) {
    const params = new URLSearchParams()
    params.set('query', this.#state.query.trim())
    params.set('clickAnalytics', 'true')
    params.set('ruleContexts', JSON.stringify(['search']))
    params.set('userToken', algoliaUserToken(this.#config))

    if (resource !== 'product') {
      params.set('highlightPreTag', '__ais-highlight__')
      params.set('highlightPostTag', '__/ais-highlight__')
    }

    return params
  }

  #render(results) {
    const [
      productsResult = {},
      productFacetsResult = productsResult,
      collectionsResult = {},
      articlesResult = {},
      pagesResult = {},
      suggestionsResult = {},
      trendingResult = {},
    ] = results
    const counts = {
      product: productsResult.nbHits || 0,
      collection: collectionsResult.nbHits || 0,
      article: articlesResult.nbHits || 0,
      page: pagesResult.nbHits || 0,
    }

    if (
      counts[this.#state.resource] === 0 &&
      this.#state.resource !== 'product'
    ) {
      this.#state.resource = 'product'
    }

    this.#renderTitle(counts)
    this.#renderSwitcher(counts)
    this.#renderProductToolbar(counts.product, productFacetsResult)
    this.#renderDrawer(productFacetsResult)
    this.#renderActiveFilters()
    this.#renderProducts(productsResult)
    this.#renderResources('collection', collectionsResult)
    this.#renderResources('article', articlesResult)
    this.#renderResources('page', pagesResult)
    this.#renderEmptyState(productsResult, suggestionsResult, trendingResult)
    this.#renderPanels()
  }

  #renderTitle(counts) {
    const title = this.querySelector('[data-main-search-title]')
    const total = this.#totalCount(counts)

    if (!title) {
      return
    }

    title.textContent = this.#state.query
      ? this.#config.strings.title
          .replace('__num__', total)
          .replace('__query__', this.#state.query)
      : this.#config.strings.searchNoQuery
  }

  #renderSwitcher(counts) {
    const switcher = this.querySelector('[data-main-search-switcher]')
    const total = this.#totalCount(counts)
    const types = ['product', 'collection', 'article', 'page']

    if (!switcher || total <= 0) {
      if (switcher) {
        switcher.hidden = true
        switcher.innerHTML = ''
      }
      return
    }

    switcher.hidden = false
    switcher.innerHTML = `
      <div class="content-switcher__content">
        <div class="content-switcher__group">
          ${types
            .filter(type => counts[type] > 0 || type === 'product')
            .map(
              type => `
                <button class="content-switcher__button text-body-md ${this.#state.resource === type ? 'is-active' : ''}" type="button" data-resource-type="${type}">
                  ${this.#escapeHtml(this.#contentTypeLabel(type, counts[type]))}
                </button>
              `
            )
            .join('')}
        </div>
        <span class="content-switcher__results text-body-md">
          ${this.#escapeHtml(`${total} ${this.#config.strings.results}`)}
        </span>
      </div>
    `
  }

  #renderProductToolbar(productCount, response) {
    const toolbar = this.querySelector('[data-main-search-product-toolbar]')
    const activeSort =
      this.#config.algolia.sortByOptions.find(
        option => option.value === this.#state.sortIndex
      ) || this.#config.algolia.sortByOptions[0]

    if (!toolbar || productCount <= 0) {
      if (toolbar) {
        toolbar.hidden = true
      }
      return
    }

    toolbar.hidden = false
    toolbar.innerHTML = `
      <button class="main-search__filters" type="button" data-main-search-mobile-filters>
        <span class="main-search__filter-icon" aria-hidden="true">
          ${this.#config.icons?.filter || this.#icon('filter')}
        </span>
        <span class="algolia-filter__button-mobile text-body-sm">
          ${this.#escapeHtml(this.#config.strings.filter)}
        </span>
      </button>
      <div class="main-search__sort-input">
        <div class="algolia-filter">
          <div class="algolia-filter__inner">
            <div class="algolia-filter__facets-wrap">
              ${this.#resolvedFilterOptions()
                .map(filter => this.#renderFacet(filter, response))
                .join('')}
            </div>
            <div class="algolia-filter__controls">
              <div class="main-search__facet">
                <button class="main-search__sort-button text-eyebrow-2xs" type="button" data-search-panel-toggle="sort" aria-expanded="false">
                  <span>${this.#escapeHtml(activeSort?.label || this.#config.strings.sortBy)}</span>
                  ${this.#icon('chevron-down')}
                </button>
                <div class="main-search__sort-panel" data-search-panel="sort" role="radiogroup" hidden>
                  ${this.#config.algolia.sortByOptions
                    .map(
                      sort => `
                        <button class="main-search__sort-option text-body-sm ${sort.value === this.#state.sortIndex ? 'is-refined' : ''}" type="button" data-sort-value="${this.#escapeHtml(sort.value)}" role="radio" aria-checked="${sort.value === this.#state.sortIndex ? 'true' : 'false'}">
                          ${this.#escapeHtml(sort.label)}
                        </button>
                      `
                    )
                    .join('')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.#positionOpenPanels()
  }

  #renderDrawer(response) {
    const drawer = this.querySelector('[data-main-search-drawer]')
    const content = this.querySelector('[data-main-search-drawer-content]')

    if (!drawer || !content) {
      return
    }

    content.innerHTML = this.#resolvedFilterOptions()
      .map(filter => this.#renderFacet(filter, response, { mobile: true }))
      .join('')

    this.#syncMobileAccordions()
  }

  #renderFacet(filter, response, options = {}) {
    const { mobile = false } = options
    const attribute = filter.value
    const panelId = `${mobile ? 'mobile-' : ''}facet-${this.#handleize(attribute)}`
    const isOpen = this.#state.openPanel === panelId

    if (isPriceAttribute(attribute)) {
      return this.#renderRangeFacet(filter, response, panelId, {
        isOpen,
        mobile,
      })
    }

    const isColour = this.#isColourAttribute(attribute)
    const facetValues = Object.entries(response?.facets?.[attribute] || {})
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count)

    if (!facetValues.length) {
      return ''
    }
    const panelClasses = [
      'main-search__facet-panel',
      'main-search__facet-panel--options',
      isColour ? 'main-search__facet-panel--colour' : '',
      mobile && isOpen ? 'is-open' : '',
    ]
      .filter(Boolean)
      .join(' ')
    const wrapperClasses = [
      'main-search__facet',
      mobile && isOpen ? 'is-active' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return `
      <div class="${this.#escapeHtml(wrapperClasses)}" data-facet="${this.#escapeHtml(attribute)}">
        <button class="main-search__facet-button text-eyebrow-2xs" type="button" data-search-panel-toggle="${this.#escapeHtml(panelId)}" aria-expanded="${isOpen ? 'true' : 'false'}">
          <span>${this.#escapeHtml(filter.label)}</span>
          ${this.#icon('chevron-down')}
        </button>
        <div class="${this.#escapeHtml(panelClasses)}" data-search-panel="${this.#escapeHtml(panelId)}" ${mobile || isOpen ? '' : 'hidden'}>
          <ul class="main-search__facet-list ${isColour ? 'main-search__facet-list--colour' : ''}" role="list">
            ${facetValues.map(facet => this.#renderFacetValue(attribute, facet)).join('')}
          </ul>
        </div>
      </div>
    `
  }

  #renderRangeFacet(filter, response, panelId, options = {}) {
    const { isOpen = false, mobile = false } = options
    const attribute = filter.value
    const stats = response?.facets_stats?.[attribute]

    if (!stats || stats.min === stats.max) {
      return ''
    }

    const selected = this.#state.ranges[attribute] || {}
    const bounds = rangeBounds(stats)
    const min = selected.min ?? Math.floor(stats.min)
    const max = selected.max ?? Math.ceil(stats.max)
    const ticks = rangeTicks(bounds.min, bounds.max)
    const wrapperClasses = [
      'main-search__facet',
      mobile && isOpen ? 'is-active' : '',
    ]
      .filter(Boolean)
      .join(' ')
    const panelClasses = [
      'main-search__facet-panel',
      'main-search__facet-panel--range',
      mobile && isOpen ? 'is-open' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return `
      <div class="${this.#escapeHtml(wrapperClasses)}" data-facet="${this.#escapeHtml(attribute)}">
        <button class="main-search__facet-button text-eyebrow-2xs" type="button" data-search-panel-toggle="${this.#escapeHtml(panelId)}" aria-expanded="${isOpen ? 'true' : 'false'}">
          <span>${this.#escapeHtml(filter.label)}</span>
          ${this.#icon('chevron-down')}
        </button>

        <div class="${this.#escapeHtml(panelClasses)}" data-search-panel="${this.#escapeHtml(panelId)}" ${mobile || isOpen ? '' : 'hidden'}>
          <div
            class="algolia-range-slider"
            data-range-slider
            data-range-attribute="${this.#escapeHtml(attribute)}"
            data-range-min="${this.#escapeHtml(bounds.min)}"
            data-range-max="${this.#escapeHtml(bounds.max)}"
            style="${rangeStyle(min, max, bounds)}"
          >
            <div class="algolia-range-slider__inputs">
              ${this.#rangeTextInput(attribute, 'min', min, bounds.min, max)}
              <div class="algolia-range-slider__inputs-divider" aria-hidden="true"></div>
              ${this.#rangeTextInput(attribute, 'max', max, min, bounds.max)}
            </div>

            <div class="algolia-range-slider__wrap" role="group" aria-label="${this.#escapeHtml(filter.label)}">
              <label class="main-search__visually-hidden" for="${this.#escapeHtml(panelId)}-min">${this.#escapeHtml(this.#config.strings.priceMin)}</label>
              <input
                id="${this.#escapeHtml(panelId)}-min"
                aria-label="${this.#escapeHtml(this.#config.strings.priceMin)}"
                data-range-input
                data-range-attribute="${this.#escapeHtml(attribute)}"
                data-range-side="min"
                type="range"
                min="${this.#escapeHtml(bounds.min)}"
                max="${this.#escapeHtml(bounds.max)}"
                step="1"
                value="${this.#escapeHtml(min)}"
              >
              <label class="main-search__visually-hidden" for="${this.#escapeHtml(panelId)}-max">${this.#escapeHtml(this.#config.strings.priceMax)}</label>
              <input
                id="${this.#escapeHtml(panelId)}-max"
                aria-label="${this.#escapeHtml(this.#config.strings.priceMax)}"
                data-range-input
                data-range-attribute="${this.#escapeHtml(attribute)}"
                data-range-side="max"
                type="range"
                min="${this.#escapeHtml(bounds.min)}"
                max="${this.#escapeHtml(bounds.max)}"
                step="1"
                value="${this.#escapeHtml(max)}"
              >
            </div>

            <div class="algolia-range-slider__indicators">
              ${ticks
                .map(
                  tick => `
                    <div class="algolia-range-slider__indicators-notch">
                      <div class="algolia-range-slider__indicators-line"></div>
                      <p class="algolia-range-slider__indicators-value text-body-sm">${this.#escapeHtml(tick)}</p>
                    </div>
                  `
                )
                .join('')}
            </div>
          </div>

          <div class="main-search__range-footer">
            <p class="main-search__range-highest text-eyebrow-2xs">
              ${this.#escapeHtml(this.#config.strings.highestPrice.replace('__price__', this.#formatMoney(stats.max)))}
            </p>

            <button class="main-search__range-reset text-eyebrow-2xs" type="button" data-range-reset="${this.#escapeHtml(attribute)}">
              ${this.#escapeHtml(this.#config.strings.reset)}
            </button>
          </div>
        </div>
      </div>
    `
  }

  #rangeTextInput(attribute, side, value, min, max) {
    const label =
      side === 'min'
        ? this.#config.strings.priceMin
        : this.#config.strings.priceMax

    return `
      <div class="algolia-range-slider__inputs-wrapper">
        <span class="algolia-range-slider__inputs-currency text-eyebrow-2xs">${this.#escapeHtml(this.#config.market.currencySymbol || '')}</span>
        <input
          aria-label="${this.#escapeHtml(label)}"
          class="algolia-range-slider__inputs-input text-body-sm"
          data-range-text
          data-range-attribute="${this.#escapeHtml(attribute)}"
          data-range-side="${this.#escapeHtml(side)}"
          inputmode="numeric"
          min="${this.#escapeHtml(min)}"
          max="${this.#escapeHtml(max)}"
          type="text"
          value="${this.#escapeHtml(value)}"
        >
      </div>
    `
  }

  #renderFacetValue(attribute, facet) {
    const selected =
      this.#state.facets[attribute]?.includes(facet.value) || false
    const isColour = this.#isColourAttribute(attribute)
    const label = this.#formatFacetLabel(attribute, facet.value)

    return `
      <li>
        <button
          class="main-search__facet-option ${isColour ? 'main-search__facet-option--colour' : ''} text-body-sm ${selected ? 'is-refined' : ''}"
          type="button"
          data-facet-attribute="${this.#escapeHtml(attribute)}"
          data-facet-value="${this.#escapeHtml(facet.value)}"
          aria-label="${this.#escapeHtml(`${label} (${String(facet.count).padStart(2, '0')})`)}"
        >
          ${isColour ? this.#renderFacetSwatch(facet.value) : '<span class="main-search__facet-checkbox"></span>'}
          <span class="${isColour ? 'main-search__visually-hidden' : ''}">${this.#escapeHtml(label)}</span>
          <span class="main-search__facet-count">(${String(facet.count).padStart(2, '0')})</span>
        </button>
      </li>
    `
  }

  #renderProducts(response) {
    const grid = this.querySelector('[data-main-search-products]')
    const pagination = this.querySelector(
      '[data-main-search-pagination="product"]'
    )
    const hits = response.hits || []

    if (!grid) {
      return
    }

    if (!hits.length) {
      grid.innerHTML = ''
      this.#renderPagination(pagination, response, 'product')
      return
    }

    const products = hits.map((hit, index) =>
      this.#formatProduct(hit, {
        indexName: this.#state.sortIndex,
        position:
          (response.page || 0) *
            (response.hitsPerPage || this.#state.pageSize) +
          index +
          1,
        queryID: response.queryID,
      })
    )
    grid.innerHTML = products
      .map((product, index) => this.#cardRenderer.render(product, index))
      .join('')
    void this.#hydrateProductSwatches(grid, products)

    this.#renderPagination(pagination, response, 'product')
  }

  #renderActiveFilters() {
    const existing = this.querySelector('[data-main-search-active-filters]')
    const drawerContainer = this.querySelector(
      '[data-main-search-drawer-active-filters]'
    )
    const drawerClear = this.querySelector('.main-search__drawer-clear')
    const facetFilters = Object.entries(this.#state.facets).flatMap(
      ([attribute, values]) =>
        values.map(value => ({
          attribute,
          label: this.#formatFacetLabel(attribute, value),
          value,
        }))
    )
    const rangeFilters = Object.entries(this.#state.ranges).flatMap(
      ([attribute, range]) => {
        const filters = []

        if (range.min !== null && range.min !== undefined && range.min !== '') {
          filters.push({
            attribute,
            label: `≥ ${this.#formatFacetLabel(attribute, range.min)}`,
            rangeSide: 'min',
            value: String(range.min),
          })
        }

        if (range.max !== null && range.max !== undefined && range.max !== '') {
          filters.push({
            attribute,
            label: `≤ ${this.#formatFacetLabel(attribute, range.max)}`,
            rangeSide: 'max',
            value: String(range.max),
          })
        }

        return filters
      }
    )
    const activeFilters = [...facetFilters, ...rangeFilters]
    const activeFilterButtons = className =>
      activeFilters
        .map(
          filter => `
            <button
              class="${this.#escapeHtml(className)}"
              type="button"
              data-active-filter
              data-filter-attribute="${this.#escapeHtml(filter.attribute)}"
              data-filter-value="${this.#escapeHtml(filter.value)}"
              ${filter.rangeSide ? `data-filter-range-side="${this.#escapeHtml(filter.rangeSide)}"` : ''}
            >
              <span>${this.#escapeHtml(filter.label)}</span>
              ${this.#icon('close')}
            </button>
          `
        )
        .join('')

    if (existing) {
      existing.remove()
    }

    if (!activeFilters.length) {
      if (drawerContainer) {
        drawerContainer.hidden = true
        drawerContainer.innerHTML = ''
      }
      if (drawerClear) {
        drawerClear.hidden = true
      }
      return
    }

    if (drawerContainer) {
      drawerContainer.hidden = false
      drawerContainer.innerHTML = activeFilterButtons(
        'main-search__drawer-active-filter text-eyebrow-2xs'
      )
    }
    if (drawerClear) {
      drawerClear.hidden = false
    }

    const container = document.createElement('div')
    container.className = 'main-search__active-filters container'
    container.dataset.mainSearchActiveFilters = ''
    container.innerHTML =
      activeFilterButtons('main-search__active-filter text-body-sm') +
      `
        <button class="main-search__active-clear text-body-sm" type="button" data-clear-all>
          ${this.#escapeHtml(this.#config.strings.clearAll)}
        </button>
      `

    this.querySelector('[data-main-search-product-toolbar]')?.after(container)
  }

  #renderResources(type, response) {
    const container = this.querySelector(`[data-main-search-${type}s]`)
    const pagination = this.querySelector(
      `[data-main-search-pagination="${type}"]`
    )
    const hits = response.hits || []

    if (!container) {
      return
    }

    container.innerHTML = hits
      .map(hit => {
        if (type === 'collection') {
          return this.#collectionTile(hit)
        }
        return this.#pageTile(hit, type)
      })
      .join('')
    this.#renderPagination(pagination, response, type)
  }

  #renderEmptyState(productsResult, suggestionsResult, trendingResult) {
    const empty = this.querySelector('[data-main-search-empty]')
    const trending = this.querySelector('[data-main-search-trending]')
    const trendingGrid = this.querySelector(
      '[data-main-search-trending-products]'
    )
    const hasProducts = (productsResult.nbHits || 0) > 0
    const query = this.#state.query.trim()

    if (!empty || !trending || !trendingGrid) {
      return
    }

    if (!query || hasProducts) {
      empty.hidden = true
      trending.hidden = true
      trendingGrid.innerHTML = ''
      return
    }

    const suggestions = suggestionsResult.hits || []
    empty.hidden = false
    empty.innerHTML = `
      <p class="algolia-filter__error-message container text-body-xs text-body-sm-desktop">
        ${this.#escapeHtml(this.#stripHtml(this.#config.strings.noResults).replace('__query__', query))}
      </p>
      <p class="algolia-filter__error-message container text-body-xs text-body-sm-desktop">
        ${this.#escapeHtml(this.#stripHtml(this.#config.strings.noResultsAdditional))}
      </p>
      ${
        suggestions.length
          ? `
        <div class="main-search__popular-searches">
          <p class="text-eyebrow-2xs">${this.#escapeHtml(this.#config.strings.popularSearches)}</p>
          <ul class="main-search__popular-list">
            ${suggestions
              .map(
                suggestion => `
                  <li>
                    <button class="main-search__popular-search text-eyebrow-2xs" type="button" data-popular-search="${this.#escapeHtml(suggestion.query || '')}">
                      ${this.#escapeHtml(suggestion.query || '')}
                    </button>
                  </li>
                `
              )
              .join('')}
          </ul>
        </div>
      `
          : ''
      }
    `

    const trendingProducts = (trendingResult.hits || []).map((hit, index) =>
      this.#formatProduct(hit, {
        indexName: this.#config.algolia.indexes.trendingProducts,
        position: index + 1,
        queryID: trendingResult.queryID,
      })
    )
    trending.hidden = !trendingProducts.length
    trendingGrid.innerHTML = trendingProducts
      .map((product, index) => this.#cardRenderer.render(product, index))
      .join('')
    void this.#hydrateProductSwatches(trendingGrid, trendingProducts)
  }

  #renderPagination(container, response, resource) {
    const totalPages = response.nbPages || 0
    const currentPage = this.#state.pages[resource] || 0

    if (!container || totalPages <= 1) {
      if (container) {
        container.hidden = true
        container.innerHTML = ''
      }
      return
    }

    container.hidden = false
    container.innerHTML = `
      <button class="main-search__pagination-button" type="button" data-search-page-resource="${resource}" data-search-page="${Math.max(currentPage - 1, 0)}" ${currentPage === 0 ? 'disabled' : ''}>
        ${this.#icon('chevron-left')}
      </button>
      ${this.#visiblePages(currentPage, totalPages)
        .map(page => {
          if (page === null) {
            return '<span class="main-search__pagination-ellipsis text-body-sm">...</span>'
          }

          return `<button class="main-search__pagination-button text-body-sm ${page === currentPage ? 'is-active' : ''}" type="button" data-search-page-resource="${resource}" data-search-page="${page}">${page + 1}</button>`
        })
        .join('')}
      <button class="main-search__pagination-button" type="button" data-search-page-resource="${resource}" data-search-page="${Math.min(currentPage + 1, totalPages - 1)}" ${currentPage >= totalPages - 1 ? 'disabled' : ''}>
        ${this.#icon('chevron-right')}
      </button>
    `
  }

  #renderPanels() {
    this.querySelectorAll('[data-resource-panel]').forEach(panel => {
      panel.hidden = panel.dataset.resourcePanel !== this.#state.resource
    })
    this.querySelectorAll('[data-resource-type]').forEach(button => {
      button.classList.toggle(
        'is-active',
        button.dataset.resourceType === this.#state.resource
      )
    })
  }

  async #hydrateProductSwatches(grid, products) {
    const requestId = ++this.#swatchRequestId
    const cards = [...grid.querySelectorAll('[data-product-card]')]
    const familyTags = products
      .map(product => this.#fetcher.familyTag(product))
      .filter(Boolean)

    if (!familyTags.length) {
      return
    }

    const relatedProductsByTag =
      await this.#fetcher.fetchRelatedProductsBatch(familyTags)

    if (requestId !== this.#swatchRequestId) {
      return
    }

    cards.forEach((card, index) => {
      const product = products[index]
      const familyTag = product ? this.#fetcher.familyTag(product) : ''
      const relatedProducts = relatedProductsByTag.get(familyTag) || []

      if (!product || !familyTag || !card.isConnected) {
        return
      }

      this.#cardRenderer.renderSwatches(
        card,
        product,
        this.#fetcher.filterColourProducts(product, relatedProducts)
      )
    })
  }

  #selectSwatch(swatch) {
    const card = swatch.closest('[data-product-card]')
    const product = parseJson(swatch.dataset.product)
    const currentProduct = this.#cardProduct(card)

    if (!card || !product || product.handle === currentProduct?.handle) {
      return
    }

    this.#replaceCard(card, {
      ...product,
      __indexName: currentProduct?.__indexName || product.__indexName,
      __position: currentProduct?.__position || product.__position,
      __queryID: currentProduct?.__queryID || product.__queryID,
    })
  }

  #replaceCard(card, product) {
    const grid = card.closest('.main-search__grid')
    const cards = [...(grid?.querySelectorAll('[data-product-card]') || [])]
    const index = Math.max(cards.indexOf(card), 0)
    const template = document.createElement('template')
    template.innerHTML = this.#cardRenderer.render(product, index).trim()
    const newCard = template.content.firstElementChild

    if (!newCard) {
      return
    }

    newCard.className = card.className
    card.replaceWith(newCard)
    void this.#hydrateCardSwatches(newCard, product)
  }

  async #hydrateCardSwatches(card, product) {
    const familyTag = this.#fetcher.familyTag(product)

    if (!familyTag) {
      return
    }

    const relatedProductsByTag = await this.#fetcher.fetchRelatedProductsBatch([
      familyTag,
    ])
    const relatedProducts = relatedProductsByTag.get(familyTag) || []

    if (!card.isConnected) {
      return
    }

    this.#cardRenderer.renderSwatches(
      card,
      product,
      this.#fetcher.filterColourProducts(product, relatedProducts)
    )
  }

  #collectionTile(collection) {
    return `
      <a class="main-search__collection-tile text-body-xs text-body-sm-desktop" href="${this.#escapeHtml(this.#resourceUrl('collections', collection.handle))}">
        <span>${this.#escapeHtml(collection.title || '')}</span>
        ${this.#icon('chevron-right')}
      </a>
    `
  }

  #pageTile(item, type) {
    const image = type === 'article' && item.image ? item.image : ''
    return `
      <a class="main-search__page-tile" href="${this.#escapeHtml(this.#resourceUrl(type === 'article' ? 'articles' : 'pages', item.handle, item))}">
        ${image ? `<img class="main-search__page-tile-image" src="${this.#escapeHtml(image)}" alt="${this.#escapeHtml(item.title || '')}" loading="lazy" decoding="async">` : ''}
        <div class="main-search__page-tile-content">
          <div class="main-search__page-tile-title">
            <p class="text-title-sans-sm">${this.#escapeHtml(item.title || '')}</p>
            ${this.#icon('chevron-right')}
          </div>
          ${item.body_html_safe ? `<p class="main-search__page-tile-excerpt text-body-xs">${this.#escapeHtml(this.#stripHtml(item.body_html_safe))}</p>` : ''}
        </div>
      </a>
    `
  }

  #formatProduct(hit, options = {}) {
    return this.#fetcher.formatProduct(hit, options)
  }
  #contentTypeLabel(type, total) {
    const template =
      this.#config.strings.contentSwitcher[type] ||
      this.#config.strings.contentSwitcher.type
    return template.replace('__type__', type).replace('__total__', total)
  }

  #visiblePages(currentPage, totalPages) {
    const pages = new Set([0, totalPages - 1, currentPage])
    if (currentPage > 0) pages.add(currentPage - 1)
    if (currentPage < totalPages - 1) pages.add(currentPage + 1)
    const sorted = [...pages].sort((a, b) => a - b)
    const visible = []
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) {
        visible.push(null)
      }
      visible.push(page)
    })
    return visible
  }

  #togglePanel(name) {
    if (name.startsWith('mobile-')) {
      this.#state.openPanel = this.#state.openPanel === name ? null : name
      this.#syncMobileAccordions()
      return
    }

    const panel = this.querySelector(`[data-search-panel="${name}"]`)
    const toggle = this.querySelector(`[data-search-panel-toggle="${name}"]`)

    if (!panel || !toggle) {
      return
    }

    const isOpen = panel.hidden
    this.#closePanels()
    this.#state.openPanel = isOpen ? name : null
    panel.hidden = !isOpen
    toggle.setAttribute('aria-expanded', String(isOpen))

    if (isOpen) {
      this.#positionOpenPanels()
    }
  }

  #openDrawer() {
    this.#state.drawerOpen = true
    this.#state.openPanel = null
    const drawer = this.querySelector('[data-main-search-drawer]')

    if (drawer) {
      clearTimeout(this.#drawerAnimationTimer)
      drawer.classList.remove('is-closing')
      drawer.classList.add('is-open')
    }

    this.#syncMobileAccordions()
    document.body.style.overflow = 'hidden'
    document.documentElement.classList.add('overflow-hidden')
  }

  #closeDrawer() {
    this.#state.drawerOpen = false
    const drawer = this.querySelector('[data-main-search-drawer]')

    if (drawer) {
      clearTimeout(this.#drawerAnimationTimer)
      drawer.classList.remove('is-open')
      drawer.classList.add('is-closing')
      this.#drawerAnimationTimer = setTimeout(() => {
        drawer.classList.remove('is-closing')
        document.body.style.overflow = ''
        document.documentElement.classList.remove('overflow-hidden')
      }, DRAWER_ANIMATION_MS)
      return
    }

    document.body.style.overflow = ''
    document.documentElement.classList.remove('overflow-hidden')
  }

  #toggleFacet(attribute, value) {
    if (!attribute || !value) {
      return
    }

    const values = this.#state.facets[attribute] || []

    if (values.includes(value)) {
      this.#state.facets[attribute] = values.filter(item => item !== value)
    } else {
      this.#state.facets[attribute] = [...values, value]
    }

    if (!this.#state.facets[attribute].length) {
      delete this.#state.facets[attribute]
    }

    this.#state.pages.product = 0
    this.#writeSearchUrl()
    void this.#search()
  }

  #removeActiveFilter(attribute, value, rangeSide = '') {
    if (rangeSide && this.#state.ranges[attribute]) {
      delete this.#state.ranges[attribute][rangeSide]

      if (
        !this.#state.ranges[attribute].min &&
        !this.#state.ranges[attribute].max
      ) {
        delete this.#state.ranges[attribute]
      }

      this.#state.pages.product = 0
      this.#writeSearchUrl()
      void this.#search()
      return
    }

    if (!attribute || !value || !this.#state.facets[attribute]) {
      return
    }

    this.#state.facets[attribute] = this.#state.facets[attribute].filter(
      item => item !== value
    )

    if (!this.#state.facets[attribute].length) {
      delete this.#state.facets[attribute]
    }

    this.#state.pages.product = 0
    this.#writeSearchUrl()
    void this.#search()
  }

  #clearAllFilters() {
    this.#state.facets = {}
    this.#state.ranges = {}
    this.#state.openPanel = null
    this.#state.pages.product = 0
    this.#closeDrawer()
    this.#writeSearchUrl()
    void this.#search()
  }

  #resolvedFilterOptions() {
    const options = this.#config.algolia.filterOptions?.length
      ? this.#config.algolia.filterOptions
      : this.#defaultFilterOptions()

    return resolveFilterOptions({
      filterOptions: options,
      includeFilter: filter => this.#isSearchFacet(filter),
      marketHandle: this.#config.market.handle,
    })
  }

  #defaultFilterOptions() {
    return defaultSearchFilterOptions(this.#config.strings.filterLabels)
  }

  #isSearchFacet(filter) {
    const label = String(filter.label || '').toLowerCase()
    const value = String(filter.value || '').toLowerCase()

    return label !== 'subcategory' && !value.includes('subcategory')
  }

  #closePanels() {
    const panelId = this.#state.openPanel
    this.#state.openPanel = null

    if (panelId?.startsWith('mobile-')) {
      this.#syncMobileAccordions()
      return
    }

    this.querySelectorAll('[data-search-panel]').forEach(panel => {
      panel.hidden = true
      panel.classList.remove('is-flipped')
    })
    this.querySelectorAll('[data-search-panel-toggle]').forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false')
    })
  }

  #syncMobileAccordions() {
    const drawer = this.querySelector('[data-main-search-drawer]')

    if (!drawer) {
      return
    }

    drawer
      .querySelectorAll('[data-search-panel-toggle^="mobile-"]')
      .forEach(toggle => {
        const isOpen =
          toggle.dataset.searchPanelToggle === this.#state.openPanel
        const facet = toggle.closest('.main-search__facet')
        const panel = facet?.querySelector('.main-search__facet-panel')

        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
        panel?.style.setProperty(
          '--mobile-panel-height',
          `${panel.scrollHeight}px`
        )
        facet?.classList.toggle('is-active', isOpen)
        panel?.classList.toggle('is-open', isOpen)
      })
  }

  #positionOpenPanels() {
    const toolbar = this.querySelector('[data-main-search-product-toolbar]')

    if (!toolbar) {
      return
    }

    const panels = toolbar.querySelectorAll(
      '.main-search__facet-panel, .main-search__sort-panel'
    )
    const viewportWidth = document.documentElement.clientWidth
    const gutter = 16

    panels.forEach(panel => {
      panel.classList.remove('is-flipped')

      if (panel.hidden) {
        return
      }

      const rect = panel.getBoundingClientRect()

      if (rect.right > viewportWidth - gutter) {
        panel.classList.add('is-flipped')
      }
    })
  }

  #resetPages() {
    this.#state.resource = 'product'
    this.#state.facets = {}
    this.#state.ranges = {}
    this.#state.openPanel = null
    this.#state.pages = {
      product: 0,
      collection: 0,
      article: 0,
      page: 0,
    }
  }

  #writeSearchUrl() {
    const params = new URLSearchParams(window.location.search)
    if (this.#state.query.trim()) {
      params.set('query', this.#state.query.trim())
    } else {
      params.delete('query')
    }
    params.delete('q')
    if (this.#state.pages.product > 0) {
      params.set('page', String(this.#state.pages.product + 1))
    } else {
      params.delete('page')
    }
    const sort = this.#config.algolia.sortByOptions.find(
      option => option.value === this.#state.sortIndex
    )
    if (sort && sort.id !== 'relevance') {
      params.set('sort', sort.id)
    } else {
      params.delete('sort')
    }
    this.#resolvedFilterOptions().forEach(filter => {
      const key = this.#urlKey(filter)

      if (isPriceAttribute(filter.value)) {
        const range = this.#state.ranges[filter.value]

        if (range) {
          params.set(key, `${range.min || ''}:${range.max || ''}`)
        } else {
          params.delete(key)
        }

        return
      }

      if (this.#state.facets[filter.value]?.length) {
        params.set(
          key,
          this.#state.facets[filter.value]
            .map(value => this.#cleanValue(value))
            .join(',')
        )
      } else {
        params.delete(key)
      }
    })
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      query ? `${window.location.pathname}?${query}` : window.location.pathname
    )
  }

  #setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading)
  }

  #renderError() {
    const grid = this.querySelector('[data-main-search-products]')
    if (grid) {
      grid.innerHTML = `<p class="main-search__empty text-body-md">${this.#escapeHtml(this.#config.strings.noProducts)}</p>`
    }
  }

  #totalCount(counts) {
    return Object.values(counts).reduce((sum, count) => sum + count, 0)
  }

  #resourceUrl(type, handle, item = {}) {
    if (!handle) return getLocalisedRoute(this.#config.routes.root)
    if (type === 'articles') {
      const blogHandle = item.blog?.handle || item.blog_handle || 'news'
      return getLocalisedRoute(
        `${this.#config.market.rootUrl}blogs/${blogHandle}/${handle}`
      )
    }
    return getLocalisedRoute(`${this.#config.market.rootUrl}${type}/${handle}`)
  }

  #pageFromUrl() {
    const page = new URLSearchParams(window.location.search).get('page')
    return this.#pageIndexFromParam(page)
  }

  #pageIndexFromParam(value) {
    const page = Number(value)

    if (!Number.isInteger(page) || page < 1) {
      return 0
    }

    return page - 1
  }

  #queryFromUrl() {
    const params = new URLSearchParams(window.location.search)
    return params.get('query') || ''
  }

  #facetsFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const facets = {}

    this.#resolvedFilterOptions().forEach(filter => {
      if (isPriceAttribute(filter.value)) {
        return
      }

      const value = params.get(this.#urlKey(filter))

      if (!value) {
        return
      }

      facets[filter.value] = value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    })

    return facets
  }

  #rangesFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const ranges = {}

    this.#resolvedFilterOptions().forEach(filter => {
      if (!isPriceAttribute(filter.value)) {
        return
      }

      const value = params.get(this.#urlKey(filter))

      if (!value) {
        return
      }

      const [min, max] = value.split(':')
      ranges[filter.value] = { min, max }
    })

    return ranges
  }

  #hasAlgoliaConfig() {
    return Boolean(
      this.#config.algolia.appId &&
      this.#config.algolia.apiKey &&
      this.#config.algolia.indexName
    )
  }

  #cardProduct(card) {
    return parseJson(card?.querySelector('[data-card-product]')?.textContent)
  }

  #rememberSelectedProduct(element) {
    const productId = element.closest('[data-product-card]')?.dataset.productId

    try {
      if (productId) {
        sessionStorage.setItem('selected_product_id', productId)
      }

      if (element.dataset.queryId) {
        sessionStorage.setItem('queryId', element.dataset.queryId)
      } else {
        sessionStorage.removeItem('queryId')
      }
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  async #openQuickView(button) {
    if (!this.#quickViewPromise) {
      this.#quickViewPromise = import('@lyle/algolia-product-card-quick-view')
    }

    const { AlgoliaProductCardQuickView } = await this.#quickViewPromise
    if (!this.isConnected) return

    if (!this.#quickView) {
      this.#quickView = new AlgoliaProductCardQuickView({
        callbacks: {
          cardProduct: card => this.#cardProduct(card),
          familyTag: product => this.#fetcher.familyTag(product),
          fetchProductJson: handle => this.#fetcher.fetchProductJson(handle),
          fetchRelatedProductsBatch: tags =>
            this.#fetcher.fetchRelatedProductsBatch(tags),
          filterColourProducts: (product, relatedProducts) =>
            this.#fetcher.filterColourProducts(product, relatedProducts),
          formatMoney: value => this.#formatMoney(value),
          sendConversionEvent: (quickViewButton, context) =>
            this.#sendConversionEvent(quickViewButton, context),
        },
        config: this.#config,
        icons: window.icons || {},
        root: this,
      })
    }

    await this.#quickView.open(button)
  }

  #closeQuickView() {
    this.#quickView?.close()
  }

  #selectQuickViewVariant(button) {
    this.#quickView?.selectVariant(button)
  }

  #selectQuickViewSwatch(button) {
    void this.#quickView?.selectSwatch(button)
  }

  #addQuickViewProduct(button) {
    void this.#quickView?.add(button)
  }

  #scrollQuickViewGallery() {
    this.#quickView?.scrollGallery()
  }

  #sendConversionEvent(button, context = {}) {
    const product = context.product || {}
    const variant = context.variant || {}

    return trackAlgoliaProductConversion(
      this.#config,
      this.#state.sortIndex,
      button,
      {
        currency: this.#config.market.currencyCode,
        price: variant.price || product.price?.amount,
        quantity: context.quantity || 1,
        queryID: product.__queryID || button?.dataset.queryId,
      }
    )
  }

  #stripHtml(value) {
    const element = document.createElement('div')
    element.innerHTML = value || ''
    return element.textContent || ''
  }

  #syncRangeControls(input, options = {}) {
    const { commit = false } = options
    const slider = input.closest('[data-range-slider]')

    if (!slider?.dataset.rangeAttribute) {
      return
    }

    const attribute = slider.dataset.rangeAttribute
    const minAllowed = Number(slider.dataset.rangeMin || 0)
    const maxAllowed = Number(slider.dataset.rangeMax || 0)
    const minRange = slider.querySelector(
      '[data-range-input][data-range-side="min"]'
    )
    const maxRange = slider.querySelector(
      '[data-range-input][data-range-side="max"]'
    )
    const minText = slider.querySelector(
      '[data-range-text][data-range-side="min"]'
    )
    const maxText = slider.querySelector(
      '[data-range-text][data-range-side="max"]'
    )
    const side = input.dataset.rangeSide
    let min = Number(minRange?.value || minAllowed)
    let max = Number(maxRange?.value || maxAllowed)
    const rawValue = input.value.trim()

    if (!commit && input.matches('[data-range-text]') && rawValue === '') {
      return
    }

    const inputValue = Number(rawValue)

    if (Number.isFinite(inputValue)) {
      if (side === 'min') {
        min = clamp(inputValue, minAllowed, max)
      } else {
        max = clamp(inputValue, min, maxAllowed)
      }
    }

    min = Math.round(clamp(min, minAllowed, maxAllowed))
    max = Math.round(clamp(max, minAllowed, maxAllowed))

    if (min > max) {
      if (side === 'min') {
        min = max
      } else {
        max = min
      }
    }

    if (minRange) minRange.value = String(min)
    if (maxRange) maxRange.value = String(max)
    if (minText) {
      minText.value = String(min)
      minText.max = String(max)
    }
    if (maxText) {
      maxText.value = String(max)
      maxText.min = String(min)
    }

    const bounds = {
      min: minAllowed,
      max: maxAllowed,
    }

    slider.style.setProperty('--range-start', `${rangePercent(min, bounds)}%`)
    slider.style.setProperty('--range-end', `${rangePercent(max, bounds)}%`)

    if (!commit) {
      return
    }

    this.#commitRange(attribute, min, max, bounds)
  }

  #commitRange(attribute, min, max, bounds) {
    if (!attribute) {
      return
    }

    this.#state.ranges[attribute] = {
      min: String(min),
      max: String(max),
    }

    this.#state.pages.product = 0
    this.#writeSearchUrl()
    void this.#search()
  }

  #resetRange(attribute) {
    if (!attribute) {
      return
    }

    delete this.#state.ranges[attribute]
    this.#state.pages.product = 0
    this.#writeSearchUrl()
    void this.#search()
  }

  #formatMoney(value) {
    return `${this.#config.market.currencySymbol || ''}${Math.round(Number(value) || 0)}`
  }

  #formatFacetLabel(attribute, value) {
    if (this.#isColourAttribute(attribute)) {
      const parts = String(value).split('-')

      if (parts.length > 2) {
        return `${parts[0]}/${parts[2]}`
      }

      return parts[0] || value
    }

    if (isPriceAttribute(attribute)) {
      return `${this.#config.market.currencySymbol || ''}${value}`
    }

    return String(value).replace(/-/g, ' ')
  }

  #isColourAttribute(attribute) {
    const normalized = String(attribute).toLowerCase()
    
    if (normalized.includes('metafields.custom.colour')) {
      return false
    }
    return normalized.includes('colour') || normalized === 'color'
  }

  #renderFacetSwatch(value) {
    const parts = String(value).split('-')
    const color = parts[1] || '#ffffff'
    const colorTwo = parts[3] || ''
    const gradient = colorTwo
      ? `background: linear-gradient(90deg, ${this.#escapeHtml(color)} 50%, ${this.#escapeHtml(colorTwo)} 50%)`
      : ''

    return `<span class="main-search__swatch" style="--swatch-color: ${this.#escapeHtml(color)}; ${gradient}"></span>`
  }

  #urlKey(filter) {
    return `filter.${this.#handleize(filter.value)}`
  }

  #cleanValue(value) {
    return String(value)
  }

  #handleize(value) {
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  #icon(name) {
    return window.icons?.[name] || ''
  }

  #isMobile() {
    return window.matchMedia('(width < 1024px)').matches
  }

  #escapeHtml(value) {
    return escapeHtml(value)
  }

  #parseConfig() {
    return parseJson(
      this.querySelector('[data-main-search-config]')?.textContent
    )
  }
}

if (!customElements.get('lyle-main-search')) {
  customElements.define('lyle-main-search', LyleMainSearch)
}
